import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit';
import { Project, Message, Rating } from '../types';
import { apiService, ApiErrorData } from '../services/api';

// Async thunks
export const fetchProjects = createAsyncThunk<
  Project[],
  void,
  { rejectValue: ApiErrorData }
>(
  'projects/fetchAll',
  async (_, { rejectWithValue }) => {
    try {
      return await apiService.projects.getAll();
    } catch (error) {
      console.error('Error caught in fetchProjects:', {
        error,
        errorType: typeof error,
        properties: error ? Object.getOwnPropertyNames(error) : undefined
      });

      return rejectWithValue(error as ApiErrorData);
    }
  }
);

export const fetchProject = createAsyncThunk<
  Project,
  number,
  { rejectValue: ApiErrorData }
>(
  'projects/fetchOne',
  async (id, { rejectWithValue }) => {
    try {
      return await apiService.projects.get(id);
    } catch (error) {
      console.error('Error caught in fetchProject:', {
        error,
        errorType: typeof error,
        properties: error ? Object.getOwnPropertyNames(error) : undefined
      });

      return rejectWithValue(error as ApiErrorData);
    }
  }
);

export const createProjectAsync = createAsyncThunk<
  Project,
  Omit<Project, 'id'>,
  { rejectValue: ApiErrorData }
>(
  'projects/create',
  async (project, { rejectWithValue }) => {
    try {
      return await apiService.projects.create(project);
    } catch (error) {
      console.error('Error caught in createProjectAsync:', {
        error,
        errorType: typeof error,
        properties: error ? Object.getOwnPropertyNames(error) : undefined
      });

      return rejectWithValue(error as ApiErrorData);
    }
  }
);

export const updateProjectAsync = createAsyncThunk<
  Project,
  { id: number; project: Partial<Project> },
  { rejectValue: ApiErrorData }
>(
  'projects/update',
  async ({ id, project }, { rejectWithValue }) => {
    try {
      return await apiService.projects.update(id, project);
    } catch (error) {
      console.error('Error caught in updateProjectAsync:', {
        error,
        errorType: typeof error,
        properties: error ? Object.getOwnPropertyNames(error) : undefined
      });

      return rejectWithValue(error as ApiErrorData);
    }
  }
);

export const deleteProjectAsync = createAsyncThunk<
  number,
  number,
  { rejectValue: ApiErrorData }
>(
  'projects/delete',
  async (id, { rejectWithValue }) => {
    try {
      await apiService.projects.delete(id);
      return id;
    } catch (error) {
      console.error('Error caught in deleteProjectAsync:', {
        error,
        errorType: typeof error,
        properties: error ? Object.getOwnPropertyNames(error) : undefined
      });

      return rejectWithValue(error as ApiErrorData);
    }
  }
);

export const addMessageAsync = createAsyncThunk<
  Message,
  { projectId: number; message: Omit<Message, 'id'> },
  { rejectValue: ApiErrorData }
>(
  'projects/addMessage',
  async ({ projectId, message }, { rejectWithValue }) => {
    try {
      return await apiService.messages.create(projectId, message);
    } catch (error) {
      console.error('Error caught in addMessageAsync:', {
        error,
        errorType: typeof error,
        properties: error ? Object.getOwnPropertyNames(error) : undefined
      });

      return rejectWithValue(error as ApiErrorData);
    }
  }
);

export const deleteMessagesAsync = createAsyncThunk<
  { projectId: number; messageIds: number[] },
  { projectId: number; messageIds: number[] },
  { rejectValue: ApiErrorData }
>(
  'projects/deleteMessages',
  async ({ projectId, messageIds }, { rejectWithValue }) => {
    try {
      await apiService.messages.delete(projectId, messageIds);
      return { projectId, messageIds };
    } catch (error) {
      console.error('Error caught in deleteMessagesAsync:', {
        error,
        errorType: typeof error,
        properties: error ? Object.getOwnPropertyNames(error) : undefined
      });

      return rejectWithValue(error as ApiErrorData);
    }
  }
);

export const importMessagesAsync = createAsyncThunk<
  Message[],
  { projectId: number; messages: Omit<Message, 'id'>[] },
  { rejectValue: ApiErrorData }
>(
  'projects/importMessages',
  async (params, { rejectWithValue }) => {
    try {
      return await apiService.messages.batchCreate(params.projectId, params.messages);
    } catch (error) {
      const errorObj = error as ApiErrorData;
      console.error('Error caught in importMessagesAsync:', {
        message: errorObj.message,
        status: errorObj.status,
        code: errorObj.code,
        data: errorObj.data
      });

      return rejectWithValue(errorObj);
    }
  }
);

export const updateMessageAsync = createAsyncThunk<
  Message,
  { projectId: number; messageId: number; data: Partial<Message> },
  { rejectValue: ApiErrorData }
>(
  'projects/updateMessage',
  async ({ projectId, messageId, data }, { rejectWithValue }) => {
    try {
      const updatedMessage = await apiService.messages.update(projectId, messageId, data);
      return updatedMessage;
    } catch (error) {
      console.error('Error caught in updateMessageAsync:', {
        error,
        errorType: typeof error,
        properties: error ? Object.getOwnPropertyNames(error) : undefined
      });

      return rejectWithValue(error as ApiErrorData);
    }
  }
);

interface ProjectState {
  projects: Project[];
  selectedProject: Project | null;
  currentIndex: string;
  loading: boolean;
  error: string | null;
}

const initialState: ProjectState = {
  projects: [],
  selectedProject: null,
  currentIndex: '',
  loading: false,
  error: null,
};

const projectSlice = createSlice({
  name: 'projects',
  initialState,
  reducers: {
    setSelectedProject(state, action: PayloadAction<Project | null>) {
      state.selectedProject = action.payload;
    },
    setCurrentIndex(state, action: PayloadAction<string>) {
      state.currentIndex = action.payload;
      
      // If we have a selected project of RAG type, update its indexName as well
      if (state.selectedProject && state.selectedProject.type === 'rag') {
        state.selectedProject.indexName = action.payload;
        state.selectedProject.rag_vector_db = action.payload;
      }
    },
    importMessagesIntoProject(state, action: PayloadAction<Message[]>) {
      if (state.selectedProject) {
        if (!state.selectedProject.messages) {
          state.selectedProject.messages = [];
        }
        state.selectedProject.messages = [
          ...state.selectedProject.messages,
          ...action.payload
        ];
        // Keep messages ordered by effective timestamp for consistent UI/export
        state.selectedProject.messages.sort((a, b) => {
          const at = new Date(a.timestamp || a.createdAt || '').getTime() || Number.POSITIVE_INFINITY;
          const bt = new Date(b.timestamp || b.createdAt || '').getTime() || Number.POSITIVE_INFINITY;
          return at - bt;
        });
      }
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch all projects
      .addCase(fetchProjects.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchProjects.fulfilled, (state, action) => {
        state.loading = false;
        state.projects = action.payload;
        state.error = null;
      })
      .addCase(fetchProjects.rejected, (state, action) => {
        state.loading = false;
        if (action.payload) {
          switch (action.payload.status) {
            case 401:
              state.error = 'Your session has expired. Please refresh the page.';
              break;
            case 503:
            case 504:
              state.error = 'Unable to connect to server. Please check your connection and try again.';
              break;
            default:
              state.error = action.payload.message || 'Failed to load projects';
          }
        } else {
          state.error = 'Failed to load projects. Please try again.';
        }
      })

      // Fetch single project
      .addCase(fetchProject.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchProject.fulfilled, (state, action) => {
        state.loading = false;
        
        console.log('ProjectSlice - fetchProject.fulfilled with payload:', action.payload);
        
        // Update project in projects array
        const index = state.projects.findIndex(p => p.id === action.payload.id);
        if (index !== -1) {
          state.projects[index] = action.payload;
        }
        
        // Set as selected project
        state.selectedProject = action.payload;
        
        // Update currentIndex if this is a RAG project
        if (action.payload.type === 'rag' && action.payload.indexName) {
          state.currentIndex = action.payload.indexName;
        } else if (action.payload.chat_type === 'rag' && action.payload.rag_vector_db) {
          state.currentIndex = action.payload.rag_vector_db;
        } else {
          state.currentIndex = '';
        }
        
        console.log('ProjectSlice - selectedProject set to:', state.selectedProject);
        console.log('ProjectSlice - currentIndex set to:', state.currentIndex);
        
        state.error = null;
      })
      .addCase(fetchProject.rejected, (state, action) => {
        state.loading = false;
        if (action.payload) {
          switch (action.payload.status) {
            case 401:
              state.error = 'Your session has expired. Please refresh the page.';
              break;
            case 503:
            case 504:
              state.error = 'Unable to connect to server. Please check your connection and try again.';
              break;
            default:
              state.error = action.payload.message || 'Failed to load project';
          }
        } else {
          state.error = 'Failed to load project. Please try again.';
        }
      })

      // Create project
      .addCase(createProjectAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createProjectAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.projects.push(action.payload);
        state.selectedProject = action.payload;
        state.error = null;
      })
      .addCase(createProjectAsync.rejected, (state, action) => {
        state.loading = false;
        if (action.payload) {
          switch (action.payload.status) {
            case 401:
              state.error = 'Your session has expired. Please refresh the page.';
              break;
            case 503:
            case 504:
              state.error = 'Unable to connect to server. Please check your connection and try again.';
              break;
            default:
              state.error = action.payload.message || 'Failed to create project';
          }
        } else {
          state.error = 'Failed to create project. Please try again.';
        }
      })

      // Update project
      .addCase(updateProjectAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateProjectAsync.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.projects.findIndex(p => p.id === action.payload.id);
        if (index !== -1) {
          state.projects[index] = action.payload;
        }
        if (state.selectedProject?.id === action.payload.id) {
          state.selectedProject = action.payload;
        }
        state.error = null;
      })
      .addCase(updateProjectAsync.rejected, (state, action) => {
        state.loading = false;
        if (action.payload) {
          switch (action.payload.status) {
            case 401:
              state.error = 'Your session has expired. Please refresh the page.';
              break;
            case 503:
            case 504:
              state.error = 'Unable to connect to server. Please check your connection and try again.';
              break;
            default:
              state.error = action.payload.message || 'Failed to update project';
          }
        } else {
          state.error = 'Failed to update project. Please try again.';
        }
      })

      // Delete project
      .addCase(deleteProjectAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteProjectAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.projects = state.projects.filter(p => p.id !== action.payload);
        if (state.selectedProject?.id === action.payload) {
          state.selectedProject = null;
        }
        state.error = null;
      })
      .addCase(deleteProjectAsync.rejected, (state, action) => {
        state.loading = false;
        if (action.payload) {
          switch (action.payload.status) {
            case 401:
              state.error = 'Your session has expired. Please refresh the page.';
              break;
            case 503:
            case 504:
              state.error = 'Unable to connect to server. Please check your connection and try again.';
              break;
            default:
              state.error = action.payload.message || 'Failed to delete project';
          }
        } else {
          state.error = 'Failed to delete project. Please try again.';
        }
      })

      // Add message
      .addCase(addMessageAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addMessageAsync.fulfilled, (state, action) => {
        state.loading = false;
        if (state.selectedProject && state.selectedProject.id === action.meta.arg.projectId) {
          state.selectedProject.messages = [...(state.selectedProject.messages || []), action.payload];
        }
        state.error = null;
      })
      .addCase(addMessageAsync.rejected, (state, action) => {
        state.loading = false;
        if (action.payload) {
          switch (action.payload.status) {
            case 401:
              state.error = 'Your session has expired. Please refresh the page.';
              break;
            case 503:
            case 504:
              state.error = 'Unable to connect to server. Please check your connection and try again.';
              break;
            default:
              state.error = action.payload.message || 'Failed to add message';
          }
        } else {
          state.error = 'Failed to add message. Please try again.';
        }
      })

      // Delete messages
      .addCase(deleteMessagesAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteMessagesAsync.fulfilled, (state, action) => {
        state.loading = false;
        const project = state.selectedProject;
        if (project?.id === action.payload.projectId && project.messages) {
          project.messages = project.messages.filter(
            (message) => message.id !== undefined && !action.payload.messageIds.includes(message.id)
          );
        }
        state.error = null;
      })
      .addCase(deleteMessagesAsync.rejected, (state, action) => {
        state.loading = false;
        if (action.payload) {
          switch (action.payload.status) {
            case 401:
              state.error = 'Your session has expired. Please refresh the page.';
              break;
            case 503:
            case 504:
              state.error = 'Unable to connect to server. Please check your connection and try again.';
              break;
            default:
              state.error = action.payload.message || 'Failed to delete messages';
          }
        } else {
          state.error = 'Failed to delete messages. Please try again.';
        }
      })

      // Update message (comment, rating, or other fields)
      .addCase(updateMessageAsync.pending, (state) => {
        state.loading = false; // Don't show loading for message updates
        state.error = null;
      })
      .addCase(updateMessageAsync.fulfilled, (state, action) => {
        state.loading = false;
        if (state.selectedProject?.messages) {
          const messageIndex = state.selectedProject.messages.findIndex(m => m.id === action.payload.id);
          if (messageIndex !== -1) {
            state.selectedProject.messages[messageIndex] = {
              ...state.selectedProject.messages[messageIndex],
              ...action.payload
            };
          }
        }
        state.error = null;
      })
      .addCase(updateMessageAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || 'Failed to update message';
      })

      // Import messages
      .addCase(importMessagesAsync.pending, (state) => {
        state.loading = true;
      })
      .addCase(importMessagesAsync.fulfilled, (state, action) => {
        state.loading = false;
        if (state.selectedProject) {
          if (!state.selectedProject.messages) {
            state.selectedProject.messages = [];
          }
          state.selectedProject.messages = [
            ...state.selectedProject.messages,
            ...action.payload
          ];
          // Ensure chronological order after merge
          state.selectedProject.messages.sort((a, b) => {
            const at = new Date(a.timestamp || a.createdAt || '').getTime() || Number.POSITIVE_INFINITY;
            const bt = new Date(b.timestamp || b.createdAt || '').getTime() || Number.POSITIVE_INFINITY;
            return at - bt;
          });
        }
      })
      .addCase(importMessagesAsync.rejected, (state) => {
        state.loading = false;
        // Don't set global error - let component handle it
      });
  },
});

export const { setSelectedProject, setCurrentIndex, importMessagesIntoProject } = projectSlice.actions;
export default projectSlice.reducer;
